import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Mic, MicOff, Upload, Loader2, Download, Copy, FileAudio,
  Clock, UserCircle, Activity, FileText, Languages, Loader, ArrowLeftRight, Volume2
} from "lucide-react";
import VoiceController, { useSpeechSynthesis } from "@/components/VoiceController";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

import { LANGUAGE_OPTIONS, TRANSLATE_LANGUAGES } from "@/config/languages";
import LanguageSelect from "@/components/LanguageSelect";



function getWSUrl() {
  const base = API || "";
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/ws/transcribe/live`;
}

export default function Transcribe() {
  const navigate = useNavigate();

  // Mode toggle: "live", "notetaker", or "recording"
  const [mode, setMode] = useState("live");

  // File-based transcription state (kept for backward compatibility)
  const [transcribeFile, setTranscribeFile] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState(null);
  const [transcriptions, setTranscriptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("auto");

  // Live transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveTranscripts, setLiveTranscripts] = useState([]);
  const [livePartialText, setLivePartialText] = useState("");
  const [liveStatus, setLiveStatus] = useState("idle"); // idle | connecting | recording | completed | error
  const [debugMessages, setDebugMessages] = useState([]);

  const recordingTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const liveTranscriptsRef = useRef([]);
  const liveTranslatedRef = useRef([]);
  const isRecordingRef = useRef(false);
  const autoTranslateRef = useRef(false);
  const transcribeTargetLangRef = useRef("English");
  const voiceEnabledRef = useRef(false);
  const voiceIdRef = useRef(null);
  const voiceRateRef = useRef(1.0);

  // Notetaker state (record locally, then transcribe)
  const [isNoteRecording, setIsNoteRecording] = useState(false);
  const [notetakerTime, setNotetakerTime] = useState(0);
  const [isNoteProcessing, setIsNoteProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const notetakerTimerRef = useRef(null);
  const notetakerStreamRef = useRef(null);

  // Auto-translate state
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [transcribeTargetLang, setTranscribeTargetLang] = useState("English");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [liveTranslated, setLiveTranslated] = useState([]);

  // Voice output state
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceId, setVoiceId] = useState(null);
  const [voiceRate, setVoiceRate] = useState(1.0);
  const { voices, speak: speakText, stop: stopSpeech } = useSpeechSynthesis();

  // Keep refs in sync for WebSocket closure
  useEffect(() => { autoTranslateRef.current = autoTranslate; }, [autoTranslate]);
  useEffect(() => { transcribeTargetLangRef.current = transcribeTargetLang; }, [transcribeTargetLang]);
  useEffect(() => { voiceEnabledRef.current = voiceEnabled; }, [voiceEnabled]);
  useEffect(() => { voiceIdRef.current = voiceId; }, [voiceId]);
  useEffect(() => { voiceRateRef.current = voiceRate; }, [voiceRate]);

  // Speak transcription text using selected voice
  const doSpeak = useCallback((text) => {
    if (!voiceEnabledRef.current || !text || !text.trim()) return;
    stopSpeech();
    const selectedVoice = voiceIdRef.current
      ? voices.find(v => v.name === voiceIdRef.current) || null
      : null;
    speakText(text, { voice: selectedVoice, rate: voiceRateRef.current });
  }, [voices, stopSpeech, speakText]);

  useEffect(() => {
    fetchTranscriptions();
  }, []);

  const fetchTranscriptions = async () => {
    try {
      const res = await axios.get(`${API}/api/media/transcriptions`, {
        headers: getHeaders(), withCredentials: true
      });
      setTranscriptions(res.data);
    } catch {}
  };

  // ── File-based transcription (unchanged) ──

  const handleTranscribe = async () => {
    if (!transcribeFile) {
      toast.error("Please select an audio or video file");
      return;
    }
    setTranslatedText("");
    setIsTranscribing(true);
    setTranscriptionResult(null);
    try {
      const formData = new FormData();
      formData.append("file", transcribeFile);
      formData.append("language_code", selectedLanguage);
      const res = await axios.post(`${API}/api/media/transcribe`, formData, {
        headers: getHeaders(), withCredentials: true
      });
      setTranscriptionResult(res.data);
      toast.success(`Transcription complete - ${res.data.speakers} speaker(s) detected`);
      setTranscribeFile(null);
      fetchTranscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transcription failed");
    }
    setIsTranscribing(false);
  };

  // ── Live transcription via WebSocket + AudioContext ──

  const startLiveRecording = useCallback(async () => {
    try {
      // Request mic access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setTranslatedText("");
      setLiveStatus("connecting");
      setLiveTranscripts([]);
      setLivePartialText("");
      setDebugMessages([]);
      liveTranscriptsRef.current = [];

      // Create AudioContext early to get the actual browser sample rate (16000 may not be honored)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });
      const actualSampleRate = audioContext.sampleRate;
      audioContextRef.current = audioContext;

      // Open WebSocket
      const ws = new WebSocket(getWSUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        // Send actual sample rate so backend writes correct WAV headers
        ws.send(JSON.stringify({ type: "config", sample_rate: actualSampleRate, language_code: selectedLanguage }));
        setLiveStatus("recording");
        setIsRecording(true);
        isRecordingRef.current = true;
        setRecordingTime(0);
        toast.success("Live transcription started...");
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "transcript") {
            if (msg.message_type === "FinalTranscript" && msg.text.trim()) {
              const segIndex = liveTranscriptsRef.current.length;
              liveTranscriptsRef.current = [
                ...liveTranscriptsRef.current,
                { text: msg.text, confidence: msg.confidence, final: true },
              ];
              setLiveTranscripts(liveTranscriptsRef.current);
              setLivePartialText("");

              // Auto-translate this segment in real-time
              if (autoTranslateRef.current) {
                axios.post(`${API}/api/media/translate`, {
                  content: msg.text,
                  source_language: "Auto-detect",
                  target_language: transcribeTargetLangRef.current
                }, {
                  headers: { ...getHeaders(), "Content-Type": "application/json" },
                  withCredentials: true
                }).then((res) => {
                  liveTranslatedRef.current[segIndex] = res.data.translated;
                  setLiveTranslated([...liveTranslatedRef.current]);
                  // Read the translated text aloud if voice is enabled
                  if (voiceEnabledRef.current && res.data.translated) {
                    doSpeak(res.data.translated);
                  }
                }).catch(() => {});
              }
            } else if (msg.message_type === "PartialTranscript") {
              setLivePartialText(msg.text);
            }
          } else if (msg.type === "session_started") {
            // Session ready — start audio capture with pre-created AudioContext
            startAudioCapture(stream, ws, audioContext);
          } else if (msg.type === "completed") {
            setTranscriptionResult({
              transcription: msg.text,
              plain_text: msg.text,
              speakers: 1,
              duration_seconds: recordingTime,
              confidence: 0,
              id: msg.transcription_id,
            });
            setLiveStatus("completed");
            fetchTranscriptions();
          } else if (msg.type === "debug") {
            setDebugMessages(prev => [...prev.slice(-19), `[${new Date().toLocaleTimeString()}] ${msg.message}`]);
          } else if (msg.type === "error") {
            toast.error(msg.detail || "Transcription error");
            setLiveStatus("error");
          }
        } catch {}
      };

      ws.onerror = () => {
        toast.error("WebSocket connection failed");
        setLiveStatus("error");
        cleanupLiveRecording();
      };

      ws.onclose = () => {
        if (isRecordingRef.current) {
          cleanupLiveRecording();
        }
      };

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      toast.error("Could not access microphone. Please check permissions.");
      setLiveStatus("idle");
    }
  }, []);

  const startAudioCapture = (stream, ws, existingAudioContext) => {
    try {
      const audioContext = existingAudioContext || new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const inputData = event.inputBuffer.getChannelData(0);
        // Convert Float32 to Int16 PCM
        const pcm = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        ws.send(pcm.buffer);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (err) {
      toast.error("Audio capture setup failed");
      cleanupLiveRecording();
    }
  };

  const stopLiveRecording = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop" }));
    }
    // Don't cleanup immediately — let the backend process the "stop" message
    // and send back completed/error. The ws.onclose handler will do cleanup.
    isRecordingRef.current = false;
    setIsRecording(false);
    setLiveStatus("completed");
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    toast.info("Processing live transcription...");
  };

  const cleanupLiveRecording = () => {
    // Stop audio processor
    if (processorRef.current && audioContextRef.current) {
      try {
        processorRef.current.disconnect();
      } catch {}
    }
    // Close audio context
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {}
    }
    // Stop media tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    // Close WebSocket
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN ||
            wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
      } catch {}
    }
    // Clear timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    setIsRecording(false);
    isRecordingRef.current = false;
    audioContextRef.current = null;
    processorRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupLiveRecording();
    };
  }, []);

  // ── Notetaker: record locally, then upload for transcription ──

  const startNoteRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      notetakerStreamRef.current = stream;

      audioChunksRef.current = [];
      setIsNoteRecording(true);
      setNotetakerTime(0);
      setTranscriptionResult(null);

      notetakerTimerRef.current = setInterval(() => {
        setNotetakerTime(prev => prev + 1);
      }, 1000);

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType;
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        uploadNoteRecording(audioBlob);
      };

      mediaRecorder.start();
      toast.success("Recording... tap Stop when done");
    } catch (err) {
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  const stopNoteRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (notetakerTimerRef.current) {
      clearInterval(notetakerTimerRef.current);
      notetakerTimerRef.current = null;
    }
    if (notetakerStreamRef.current) {
      notetakerStreamRef.current.getTracks().forEach(t => t.stop());
      notetakerStreamRef.current = null;
    }
    setIsNoteRecording(false);
    setIsNoteProcessing(true);
    toast.info("Uploading recording for transcription...");
  };

  const uploadNoteRecording = async (audioBlob) => {
    try {
      const formData = new FormData();
      const ext = audioBlob.type.includes("webm") ? "webm" : "mp4";
      formData.append("file", audioBlob, `notetaker_${Date.now()}.${ext}`);
      formData.append("language_code", selectedLanguage);

      const res = await axios.post(`${API}/api/media/transcribe`, formData, {
        headers: getHeaders(), withCredentials: true,
      });
      setTranscriptionResult(res.data);
      toast.success(`Transcription complete - ${res.data.speakers} speaker(s) detected`);
      fetchTranscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transcription failed");
    }
    setIsNoteProcessing(false);
  };

  // ── Auto-translate ──

  // Translate a single live segment as it arrives
  const translateLiveSegment = useCallback(async (text, segmentIndex) => {
    if (!text || !text.trim() || !autoTranslate) return;
    try {
      const res = await axios.post(`${API}/api/media/translate`, {
        content: text,
        source_language: "Auto-detect",
        target_language: transcribeTargetLang
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      const translated = res.data.translated;
      // Store at the correct index (may have shifted if multiple segments arrive)
      liveTranslatedRef.current[segmentIndex] = translated;
      setLiveTranslated([...liveTranslatedRef.current]);
    } catch (err) {
      // Silent fail for live segment translation
    }
  }, [autoTranslate, transcribeTargetLang]);

  // Translate full transcription (for post-recording / notetaker / file modes)
  const handleTranslateResult = async (text) => {
    if (!text || !text.trim()) return;
    setIsTranslating(true);
    try {
      const res = await axios.post(`${API}/api/media/translate`, {
        content: text,
        source_language: "Auto-detect",
        target_language: transcribeTargetLang
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      setTranslatedText(res.data.translated);
      toast.success(`Translated to ${transcribeTargetLang}`);
    } catch (err) {
      toast.error("Translation failed");
    }
    setIsTranslating(false);
  };

  // Auto-translate when transcription completes or changes
  useEffect(() => {
    if (autoTranslate && transcriptionResult && transcriptionResult.transcription) {
      handleTranslateResult(transcriptionResult.transcription);
    } else if (!autoTranslate) {
      setTranslatedText("");
    }
  }, [autoTranslate, transcriptionResult?.transcription]);

  // Speak the translated full text when it appears (notetaker/recording/live-completed)
  useEffect(() => {
    if (voiceEnabled && translatedText) {
      doSpeak(translatedText);
    }
  }, [translatedText]);

  // Cleanup notetaker on unmount
  useEffect(() => {
    return () => {
      if (notetakerStreamRef.current) {
        notetakerStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center">
              <Mic className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
                TRANSCRIBE
              </h1>
              <p className="text-slate-600">Live & file-based transcription with AssemblyAI</p>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-slate-200 rounded-lg p-0.5">
            <button
              onClick={() => setMode("live")}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                mode === "live"
                  ? "bg-white text-rose-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Mic className="w-3.5 h-3.5 inline mr-1.5" />
              Live
            </button>
            <button
              onClick={() => setMode("notetaker")}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                mode === "notetaker"
                  ? "bg-white text-rose-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <FileText className="w-3.5 h-3.5 inline mr-1.5" />
              Notetaker
            </button>
            <button
              onClick={() => setMode("recording")}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                mode === "recording"
                  ? "bg-white text-rose-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Upload className="w-3.5 h-3.5 inline mr-1.5" />
              Recording
            </button>
          </div>
        </div>
      </div>

      {/* Audio Language Selection + Auto-Translate */}
      <div className="mb-6 bg-white border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Languages className="w-4 h-4" />
            Audio language:
          </div>
          <LanguageSelect value={selectedLanguage} onValueChange={setSelectedLanguage} includeAuto={true} />

          <div className="h-6 w-px bg-slate-300" />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoTranslate}
              onChange={(e) => {
                setAutoTranslate(e.target.checked);
                if (!e.target.checked) setTranslatedText("");
              }}
              className="w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
            />
            <span className="text-sm text-slate-600 font-medium">Auto-translate</span>
          </label>

          {autoTranslate && (
            <>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>to</span>
                <LanguageSelect value={transcribeTargetLang} onValueChange={setTranscribeTargetLang}
                  includeAuto={false} placeholder="Target language" />
              </div>
            </>
          )}

          <div className="h-6 w-px bg-slate-300" />

          {/* Voice Output */}
          <div className="relative flex items-center gap-2">
            <button
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                voiceEnabled
                  ? "bg-violet-100 text-violet-700 font-medium"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              }`}
              title={voiceEnabled ? "Voice on" : "Voice off"}
            >
              <Volume2 className="w-4 h-4" />
              <span>{voiceEnabled ? "Voice On" : "Voice"}</span>
            </button>
            {voiceEnabled && (
              <div className="flex items-center gap-2">
                <select
                  value={voiceId || ""}
                  onChange={(e) => setVoiceId(e.target.value || null)}
                  className="text-xs border border-slate-300 rounded px-1.5 py-1 bg-app focus:outline-none focus:ring-1 focus:ring-violet-500 max-w-[120px]"
                >
                  <option value="">Default</option>
                  {voices.filter(v => /female|woman|samantha|fiona|karen|susan|mary|jessica|sarah|emma|olivia|ava|sophia|mia|charlotte|amelia|ella|zoe|hannah|aria|scarlett|grace|chloe|victoria|riley|lena|nora|alice|freya|ivy|daisy|rose|iris|poppy|holly|jasmine|ruby|ella|alexa|monica/i.test(v.name)).map(v => (
                    <option key={v.name} value={v.name}>{v.name.replace(/\s*\(.*?\)/g, "")} (F)</option>
                  ))}
                  {voices.filter(v => /male|man|david|james|john|daniel|paul|mark|donald|george|ken|mike|rich|tom|victor|will|alex|bob|charlie|frank|harry|jack|karl|leo|nick|oliver|peter|ray|sam|tim|ulf|vincent|walter|xavier|zeke/i.test(v.name)).map(v => (
                    <option key={v.name} value={v.name}>{v.name.replace(/\s*\(.*?\)/g, "")} (M)</option>
                  ))}
                </select>
                <div className="flex items-center gap-1.5">
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={voiceRate}
                    onChange={(e) => setVoiceRate(parseFloat(e.target.value))}
                    className="w-16 h-1 bg-slate-200 rounded-full appearance-none cursor-pointer accent-violet-600"
                    title={`Speed: ${voiceRate.toFixed(1)}x`}
                  />
                  <span className="text-[10px] text-slate-400 w-7">{voiceRate.toFixed(1)}x</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section — switches between Live and Recording modes */}
        <div className="space-y-6">
          {mode === "live" ? (
            /* ── Live Mode ── */
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Mic className="w-5 h-5 text-rose-500" />
                Live AI Notetaker
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                Speak now — live transcription streams in real-time as you talk
              </p>

              {/* Live Transcript Preview */}
              {(isRecording || liveTranscripts.length > 0) && (
                <div className="mb-4 border border-slate-200 bg-app rounded overflow-hidden">
                  <div className="bg-slate-100 px-3 py-1.5 border-b border-slate-200 text-[11px] font-medium text-slate-600 flex items-center gap-1.5">
                    {isRecording ? (
                      <>
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        Live — speaking now
                      </>
                    ) : (
                      <>
                        <FileText className="w-3 h-3" />
                        Transcription result
                      </>
                    )}
                  </div>
                  <div className="p-3 max-h-[200px] overflow-y-auto">
                    {liveTranscripts.map((t, i) => (
                      <div key={i} className="mb-2 last:mb-0">
                        <p className="text-sm text-slate-700">{t.text}</p>
                        {autoTranslate && liveTranslated[i] && (
                          <p className="text-xs text-violet-600 ml-2 mt-0.5">→ {liveTranslated[i]}</p>
                        )}
                      </div>
                    ))}
                    {isRecording && livePartialText && (
                      <p className="text-sm text-slate-400 italic">{livePartialText}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Debug Messages */}
              {debugMessages.length > 0 && (
                <div className="mb-3 border border-amber-200 bg-amber-50 rounded overflow-hidden">
                  <div className="bg-amber-100 px-3 py-1 border-b border-amber-200 text-[10px] font-medium text-amber-700">
                    Debug Log
                  </div>
                  <div className="p-2 max-h-[100px] overflow-y-auto">
                    {debugMessages.map((m, i) => (
                      <p key={i} className="text-[10px] text-amber-700 font-mono leading-tight">{m}</p>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center py-6">
                {isRecording ? (
                  <>
                    <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
                      <Mic className="w-10 h-10 text-red-500" />
                    </div>
                    <p className="text-2xl font-['JetBrains_Mono'] text-red-500 mb-2">
                      {formatTime(recordingTime)}
                    </p>
                    <p className="text-sm text-slate-600 mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      Transcribing in ~5s chunks...
                    </p>
                    <Button
                      onClick={stopLiveRecording}
                      className="bg-red-600 hover:bg-red-500 rounded-none px-8"
                      data-testid="stop-recording-btn"
                    >
                      <MicOff className="w-4 h-4 mr-2" />
                      Stop & Complete
                    </Button>
                  </>
                ) : liveStatus === "connecting" ? (
                  <>
                    <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                      <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                    </div>
                    <p className="text-sm text-slate-600">Connecting to live transcription...</p>
                  </>
                ) : (
                  <>
                    <div
                      className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-4 hover:bg-rose-100 transition-colors cursor-pointer"
                      onClick={startLiveRecording}
                    >
                      <Mic className="w-10 h-10 text-slate-500" />
                    </div>
                    <Button
                      onClick={startLiveRecording}
                      className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 rounded-none px-8"
                      data-testid="start-recording-btn"
                    >
                      <Mic className="w-4 h-4 mr-2" />
                      Start Live Recording
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : mode === "notetaker" ? (
            /* ── Notetaker Mode (record → upload → transcribe) ── */
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-rose-500" />
                Notetaker
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                Record audio, then stop — the full recording is transcribed at once
              </p>

              <div className="flex flex-col items-center py-6">
                {isNoteRecording ? (
                  <>
                    <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
                      <Mic className="w-10 h-10 text-red-500" />
                    </div>
                    <p className="text-2xl font-['JetBrains_Mono'] text-red-500 mb-2">
                      {formatTime(notetakerTime)}
                    </p>
                    <p className="text-sm text-slate-600 mb-4">Recording — tap Stop when finished</p>
                    <Button
                      onClick={stopNoteRecording}
                      className="bg-red-600 hover:bg-red-500 rounded-none px-8"
                    >
                      <MicOff className="w-4 h-4 mr-2" />
                      Stop & Transcribe
                    </Button>
                  </>
                ) : isNoteProcessing ? (
                  <>
                    <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                      <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                    </div>
                    <p className="text-sm text-slate-600">Transcribing recording...</p>
                  </>
                ) : (
                  <>
                    <div
                      className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-4 hover:bg-rose-100 transition-colors cursor-pointer"
                      onClick={startNoteRecording}
                    >
                      <Mic className="w-10 h-10 text-slate-500" />
                    </div>
                    <Button
                      onClick={startNoteRecording}
                      className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 rounded-none px-8"
                    >
                      <Mic className="w-4 h-4 mr-2" />
                      Start Recording
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* ── Recording Mode (File Upload) ── */
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileAudio className="w-5 h-5 text-rose-500" />
                Upload Audio/Video
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Upload files for transcription with speaker diarization
              </p>

              <div className="border-2 border-dashed border-slate-300 p-6 text-center hover:border-rose-400 transition-colors">
                <input
                  type="file"
                  accept="audio/*,video/*,.mp3,.wav,.ogg,.m4a,.flac,.mp4,.mov,.avi,.webm"
                  onChange={(e) => setTranscribeFile(e.target.files[0])}
                  className="hidden"
                  id="transcribe-upload"
                  data-testid="transcribe-file-input"
                />
                <label htmlFor="transcribe-upload" className="cursor-pointer">
                  <FileAudio className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  {transcribeFile ? (
                    <p className="text-rose-500 font-medium">{transcribeFile.name}</p>
                  ) : (
                    <>
                      <p className="text-slate-600 font-medium">Drop audio/video file here</p>
                      <p className="text-xs text-slate-500 mt-1">MP3, WAV, MP4, MOV, etc. (Max 500MB)</p>
                    </>
                  )}
                </label>
              </div>

              <Button
                onClick={handleTranscribe}
                disabled={isTranscribing || !transcribeFile}
                className="w-full mt-4 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 rounded-none"
                data-testid="transcribe-btn"
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Transcribing... (This may take a few minutes)
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Transcribe
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 mt-3 text-center">
                Powered by AssemblyAI • Speaker diarization enabled
              </p>
            </div>
          )}
        </div>

        {/* Transcription Output — shared by both modes */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Transcription Result</h3>

            {transcriptionResult ? (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-app p-3 text-center rounded">
                    <UserCircle className="w-5 h-5 text-rose-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-slate-800">{transcriptionResult.speakers}</p>
                    <p className="text-xs text-slate-500">Speakers</p>
                  </div>
                  <div className="bg-app p-3 text-center rounded">
                    <Clock className="w-5 h-5 text-rose-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-slate-800">
                      {transcriptionResult.duration_seconds ? Math.round(transcriptionResult.duration_seconds / 60) : 0}m
                    </p>
                    <p className="text-xs text-slate-500">Duration</p>
                  </div>
                  <div className="bg-app p-3 text-center rounded">
                    <Activity className="w-5 h-5 text-rose-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-slate-800">
                      {transcriptionResult.confidence ? Math.round(transcriptionResult.confidence * 100) : 0}%
                    </p>
                    <p className="text-xs text-slate-500">Confidence</p>
                  </div>
                </div>

                {/* Transcription Text */}
                <ScrollArea className="h-[200px] border border-slate-200">
                  <div className="p-4">
                    <pre className="whitespace-pre-wrap text-slate-700 font-['IBM_Plex_Sans'] text-sm leading-relaxed">
                      {transcriptionResult.transcription}
                    </pre>
                  </div>
                </ScrollArea>

                {/* Translated Text */}
                {translatedText && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1">
                      <Languages className="w-3.5 h-3.5" />
                      {transcribeTargetLang} Translation
                    </h4>
                    <ScrollArea className="h-[150px] border border-slate-200 bg-app">
                      <div className="p-4">
                        <pre className="whitespace-pre-wrap text-slate-700 font-['IBM_Plex_Sans'] text-sm leading-relaxed">
                          {translatedText}
                        </pre>
                      </div>
                    </ScrollArea>
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-none"
                    onClick={() => {
                      navigator.clipboard.writeText(transcriptionResult.transcription);
                      toast.success("Transcription copied");
                    }}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Copy with Speakers
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-none"
                    onClick={() => {
                      navigator.clipboard.writeText(transcriptionResult.plain_text || transcriptionResult.transcription);
                      toast.success("Plain text copied");
                    }}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Copy Plain Text
                  </Button>
                  {mode !== "live" && !autoTranslate && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-none border-rose-300 text-rose-600 hover:bg-rose-50"
                      onClick={() => handleTranslateResult(transcriptionResult.transcription)}
                      disabled={isTranslating}
                    >
                      {isTranslating ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Languages className="w-4 h-4 mr-1" />
                      )}
                      Translate to {transcribeTargetLang}
                    </Button>
                  )}
                  {translatedText && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={() => {
                        navigator.clipboard.writeText(translatedText);
                        toast.success("Translation copied");
                      }}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Translation
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-[450px] flex items-center justify-center border border-dashed border-slate-300">
                <div className="text-center">
                  <Mic className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Transcription will appear here</p>
                  <p className="text-xs text-slate-400 mt-1">With speaker identification</p>
                </div>
              </div>
            )}
          </div>

          {/* Previous Transcriptions */}
          {transcriptions.length > 0 && (
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Previous Transcriptions</h3>
              <div className="space-y-2">
                {transcriptions.slice(0, 5).map((trans) => (
                  <div
                    key={trans.id}
                    className="p-3 bg-app border border-slate-200 hover:border-rose-400 cursor-pointer transition-colors"
                    onClick={() => setTranscriptionResult({
                      transcription: trans.transcription,
                      plain_text: trans.plain_text,
                      speakers: trans.speakers,
                      duration_seconds: trans.duration_seconds,
                      confidence: trans.confidence
                    })}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileAudio className="w-4 h-4 text-rose-500" />
                      <span className="text-sm font-medium text-slate-700 truncate">
                        {trans.filename}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <UserCircle className="w-3 h-3" />
                        {trans.speakers} speaker(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {trans.duration_seconds ? Math.round(trans.duration_seconds / 60) : 0}m
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
